import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { showToast } from '../store/toaster/action';
import { useAuth0 } from '../__mocks__/@auth0/auth0-react';

const useApiInterceptor = () => {
  const dispatch = useDispatch();
  const { logout, getAccessTokenSilently, getIdTokenClaims } = useAuth0();

  useEffect(() => {
    const handleSessionExpired = async (error: any) => {
      dispatch(showToast('Session expired. Please log in again.', 'error'));
      await new Promise(resolve => setTimeout(resolve, 3000));
      logout({ logoutParams: { returnTo: window.location.origin } });
      return Promise.reject(error);
    };

    const interceptorId = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error?.config;
        if (
          error?.response?.status !== 401 ||
          !originalRequest ||
          originalRequest._retry
        ) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        let idToken: string | undefined;
        try {
          // Force a renewal — the cached token just got rejected.
          await getAccessTokenSilently({ cacheMode: 'off' });
          const claims: any = await getIdTokenClaims();
          idToken = claims?.__raw;
        } catch {
          idToken = undefined;
        }
        if (!idToken) {
          // Renewal failed: refresh token revoked/expired — session is dead.
          return handleSessionExpired(error);
        }

        window.localStorage.setItem('userToken', idToken);
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${idToken}`,
        };
        if (originalRequest.headers['X-Authorization']) {
          originalRequest.headers['X-Authorization'] = idToken;
        }
        try {
          return await axios(originalRequest);
        } catch (retryError: any) {
          if (retryError?.response?.status === 401) {
            // Fresh token still rejected — not an expiry problem the client
            // can fix; treat as a dead session.
            return handleSessionExpired(retryError);
          }
          return Promise.reject(retryError);
        }
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [dispatch, logout, getAccessTokenSilently, getIdTokenClaims]);
};

export default useApiInterceptor;
